import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [showTryButton, setShowTryButton] = useState(false);
  const savedScrollY = useRef(0);

  const languages = [
    { code: 'en', name: t('languages.en') },
    { code: 'fr', name: t('languages.fr') },
    { code: 'es', name: t('languages.es') },
    { code: 'zh', name: t('languages.zh') }
  ];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLangMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['features', 'pricing', 'about'];
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      // Check if scrolled to bottom
      const isAtBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 100;

      const featuresElement = document.getElementById('features');
      if (featuresElement && scrollPosition < featuresElement.offsetTop) {
        setActiveSection('');
        setShowTryButton(false);
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname);
        }
        return;
      }

      setShowTryButton(true);

      // If at bottom, always set about as active
      if (isAtBottom) {
        setActiveSection('about');
        if (window.location.hash !== '#about') {
          window.history.replaceState(null, '', '#about');
        }
        return;
      }

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            if (window.location.hash !== `#${sectionId}`) {
              window.history.replaceState(null, '', `#${sectionId}`);
            }
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isLangMenuOpen && !target.closest('.language-switcher')) {
        setIsLangMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLangMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      savedScrollY.current = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollY.current}px`;
      document.body.style.width = '100%';
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY.current);
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleMobileNavClick = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };


  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-logo">
          <img src="/logo.svg" alt="CypressEra Logo" className="navbar-logo-icon" />
          <span className="navbar-logo-text">CypressEra</span>
        </div>

        {/* Desktop Navigation */}
        <div className="navbar-links">
          <a
            href="#features"
            className={`navbar-link ${activeSection === 'features' ? 'active' : ''}`}
            onClick={scrollToSection('features')}
          >
            {t('navbar.features')}
          </a>
          <a
            href="#pricing"
            className={`navbar-link ${activeSection === 'pricing' ? 'active' : ''}`}
            onClick={scrollToSection('pricing')}
          >
            {t('navbar.pricing')}
          </a>
          <a
            href="#about"
            className={`navbar-link ${activeSection === 'about' ? 'active' : ''}`}
            onClick={scrollToSection('about')}
          >
            {t('navbar.about')}
          </a>
          {showTryButton && (
            <a href="https://flow.cypressera.ai" target="_blank" rel="noopener noreferrer" className="navbar-try-button">
              {t('hero.tryNow')}
            </a>
          )}
          <div className="language-switcher">
            <button
              className="language-button"
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              aria-label="Change language"
            >
              <svg fill="currentColor" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                <title>ionicons-v5-l</title>
                <path d="M478.33,433.6l-90-218a22,22,0,0,0-40.67,0l-90,218a22,22,0,1,0,40.67,16.79L316.66,406H419.33l18.33,44.39A22,22,0,0,0,458,464a22,22,0,0,0,20.32-30.4ZM334.83,362,368,281.65,401.17,362Z"></path>
                <path d="M267.84,342.92a22,22,0,0,0-4.89-30.7c-.2-.15-15-11.13-36.49-34.73,39.65-53.68,62.11-114.75,71.27-143.49H330a22,22,0,0,0,0-44H214V70a22,22,0,0,0-44,0V90H54a22,22,0,0,0,0,44H251.25c-9.52,26.95-27.05,69.5-53.79,108.36-31.41-41.68-43.08-68.65-43.17-68.87a22,22,0,0,0-40.58,17c.58,1.38,14.55,34.23,52.86,83.93.92,1.19,1.83,2.35,2.74,3.51-39.24,44.35-77.74,71.86-93.85,80.74a22,22,0,1,0,21.07,38.63c2.16-1.18,48.6-26.89,101.63-85.59,22.52,24.08,38,35.44,38.93,36.1a22,22,0,0,0,30.75-4.9Z"></path>
              </svg>
            </button>
            {isLangMenuOpen && (
              <div className="language-dropdown">
                {languages.map(lang => (
                  <button
                    key={lang.code}
                    className={`language-option ${i18n.language === lang.code ? 'active' : ''}`}
                    onClick={() => changeLanguage(lang.code)}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-content">
          <div className="mobile-menu-links">
            <a
              href="#features"
              className={`mobile-menu-link ${activeSection === 'features' ? 'active' : ''}`}
              onClick={handleMobileNavClick('features')}
            >
              {t('navbar.features')}
            </a>
            <a
              href="#pricing"
              className={`mobile-menu-link ${activeSection === 'pricing' ? 'active' : ''}`}
              onClick={handleMobileNavClick('pricing')}
            >
              {t('navbar.pricing')}
            </a>
            <a
              href="#about"
              className={`mobile-menu-link ${activeSection === 'about' ? 'active' : ''}`}
              onClick={handleMobileNavClick('about')}
            >
              {t('navbar.about')}
            </a>
          </div>
          <a href="https://flow.cypressera.ai" target="_blank" rel="noopener noreferrer" className="mobile-try-button" onClick={() => setIsMobileMenuOpen(false)}>
            {t('hero.tryNow')}
          </a>
        </div>
      </div>

      {/* Backdrop for mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="mobile-menu-backdrop"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </nav>
  );
};

export default Navbar;
